<template>
  <div
    ref="imageElement"
    class="image"
    :class="{ 'is-dragging': isDragging }"
    @wheel.prevent="onWheel"
    @dblclick="toggleZoom"
    @pointerdown.prevent="onPointerDown"
    @pointermove.prevent="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @dragstart.prevent
  >
    <div
      class="image-inner"
      :class="{ 'is-dragging': isDragging }"
      :style="{
        transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
      }"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  resetKey?: number
}>()

const emit = defineEmits<{
  (e: "zoom-change", isZoomed: boolean): void
}>()

const imageElement = useTemplateRef<HTMLElement>("imageElement")

const scale = ref(1)
const pan = ref({ x: 0, y: 0 })
const isDragging = ref(false)

const pointers = new Map<number, { x: number; y: number }>()
let dragStartPointer = { x: 0, y: 0 }
let dragStartPan = { x: 0, y: 0 }

let pinchStartDistance = 0
let pinchStartScale = 1
let pinchStartPan = { x: 0, y: 0 }
let pinchStartMidpoint = { x: 0, y: 0 }
let pinchStartRect: DOMRect | null = null

const MIN = 1
const MAX = 15

const clampScale = (value: number) => Math.min(MAX, Math.max(MIN, value))

const getBounds = () => {
  const element = imageElement.value
  const rect = element?.getBoundingClientRect()
  return {
    maxX: rect ? (((scale.value - 1) * rect.width) / 2) * 1.4 : 0,
    maxY: rect ? (((scale.value - 1) * rect.height) / 2) * 1.4 : 0,
  }
}

const clampPan = () => {
  const { maxX, maxY } = getBounds()
  pan.value.x = Math.max(-maxX, Math.min(maxX, pan.value.x))
  pan.value.y = Math.max(-maxY, Math.min(maxY, pan.value.y))
}

const elasticPan = (targetX: number, targetY: number) => {
  const { maxX, maxY } = getBounds()
  const applyResistance = (value: number, limit: number) =>
    value > limit ? limit + (value - limit) * 0.3 : value < -limit ? -limit + (value + limit) * 0.3 : value
  pan.value.x = applyResistance(targetX, maxX)
  pan.value.y = applyResistance(targetY, maxY)
}

const distanceBetween = (firstPoint: { x: number; y: number }, secondPoint: { x: number; y: number }) => {
  return Math.hypot(firstPoint.x - secondPoint.x, firstPoint.y - secondPoint.y)
}

const getTwoPointers = () => {
  const values = [...pointers.values()]
  const first = values[0]
  const second = values[1]

  if (!first || !second) return null

  return [first, second] as const
}

const midpointBetween = (firstPoint: { x: number; y: number }, secondPoint: { x: number; y: number }) => {
  return {
    x: (firstPoint.x + secondPoint.x) / 2,
    y: (firstPoint.y + secondPoint.y) / 2,
  }
}

const resetPanIfNeeded = () => {
  if (scale.value === 1) {
    pan.value = { x: 0, y: 0 }
  }
}

const resetZoom = () => {
  scale.value = 1
  pan.value = { x: 0, y: 0 }
  isDragging.value = false
}

const isScrolling = ref(false)
let scrollTimeout: number | null = null

const onWheel = (event: WheelEvent) => {
  const rect = imageElement.value?.getBoundingClientRect()

  if (!rect) return

  const pointerX = event.clientX - rect.left
  const pointerY = event.clientY - rect.top

  const oldScale = scale.value
  const zoomStep = 0.2
  const newScale = clampScale(oldScale + (event.deltaY < 0 ? zoomStep : -zoomStep))
  const scaleFactor = newScale / oldScale

  pan.value = {
    x: pan.value.x * scaleFactor - (pointerX - rect.width / 2) * (scaleFactor - 1),
    y: pan.value.y * scaleFactor - (pointerY - rect.height / 2) * (scaleFactor - 1),
  }

  scale.value = newScale
  resetPanIfNeeded()

  const zoomCursor = event.deltaY < 0 ? "zoom-in" : "zoom-out"
  imageElement.value!.style.cursor = zoomCursor
  isScrolling.value = true

  if (scrollTimeout) clearTimeout(scrollTimeout)
  scrollTimeout = window.setTimeout(() => {
    if (imageElement.value) {
      imageElement.value.style.cursor = "inherit"
    }
    isScrolling.value = false
  }, 150)
}

const toggleZoom = () => {
  scale.value = scale.value > 1 ? 1 : 2
  resetPanIfNeeded()
}

const onPointerDown = (event: PointerEvent) => {
  if (event.pointerType === "mouse" && event.button !== 0) return

  pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
  ;(event.target as HTMLElement).setPointerCapture(event.pointerId)

  if (pointers.size === 1 && scale.value > 1) {
    dragStartPointer = { x: event.clientX, y: event.clientY }
    dragStartPan = { ...pan.value }
    isDragging.value = true
    return
  }

  if (pointers.size === 2) {
    const twoPointers = getTwoPointers()
    if (!twoPointers) return

    const [pointA, pointB] = twoPointers
    pinchStartDistance = distanceBetween(pointA, pointB)
    pinchStartScale = scale.value
    pinchStartPan = { ...pan.value }
    pinchStartMidpoint = midpointBetween(pointA, pointB)
    pinchStartRect = (event.currentTarget as HTMLElement).getBoundingClientRect()
    isDragging.value = false
  }
}

const onPointerMove = (event: PointerEvent) => {
  if (!pointers.has(event.pointerId)) return

  pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })

  if (pointers.size === 2) {
    const twoPointers = getTwoPointers()
    if (!twoPointers) return

    const [pointA, pointB] = twoPointers
    const nextDistance = distanceBetween(pointA, pointB)
    if (pinchStartDistance > 0 && pinchStartRect) {
      const nextMidpoint = midpointBetween(pointA, pointB)
      const nextScale = clampScale(pinchStartScale * (nextDistance / pinchStartDistance))
      const scaleFactor = nextScale / pinchStartScale

      const startPointX = pinchStartMidpoint.x - pinchStartRect.left - pinchStartRect.width / 2
      const startPointY = pinchStartMidpoint.y - pinchStartRect.top - pinchStartRect.height / 2

      pan.value = {
        x: pinchStartPan.x * scaleFactor - startPointX * (scaleFactor - 1) + (nextMidpoint.x - pinchStartMidpoint.x),
        y: pinchStartPan.y * scaleFactor - startPointY * (scaleFactor - 1) + (nextMidpoint.y - pinchStartMidpoint.y),
      }

      scale.value = nextScale
      resetPanIfNeeded()
    }
    isDragging.value = false
    return
  }

  if (pointers.size === 1 && scale.value > 1) {
    const dragMultiplier = 1.3
    elasticPan(
      dragStartPan.x + (event.clientX - dragStartPointer.x) * dragMultiplier,
      dragStartPan.y + (event.clientY - dragStartPointer.y) * dragMultiplier,
    )
    isDragging.value = true
    return
  }

  isDragging.value = false
}

const onPointerUp = (event: PointerEvent) => {
  if (pointers.has(event.pointerId)) {
    pointers.delete(event.pointerId)
  }

  if (pointers.size < 2) {
    pinchStartDistance = 0
    pinchStartRect = null
  }

  const element = event.currentTarget as HTMLElement | null
  if (element?.hasPointerCapture(event.pointerId)) {
    element.releasePointerCapture(event.pointerId)
  }

  if (pointers.size === 1 && scale.value > 1) {
    const remaining = [...pointers.values()][0]
    if (!remaining) {
      isDragging.value = false
      return
    }

    dragStartPointer = { ...remaining }
    dragStartPan = { ...pan.value }
    isDragging.value = true
    return
  }

  isDragging.value = false
  clampPan()
}

watch(
  () => scale.value > MIN,
  (isZoomed) => {
    emit("zoom-change", isZoomed)
  },
  { immediate: true },
)

watch(
  () => props.resetKey,
  () => {
    resetZoom()
  },
)

onBeforeUnmount(() => {
  pointers.clear()
  isDragging.value = false
  emit("zoom-change", false)
})
</script>

<style scoped>
.image {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  touch-action: none;
}
.image-inner {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  transform-origin: center;
  transition: transform 0.05s ease;
  cursor: inherit;
  user-select: none;
}

.image-inner.is-dragging {
  transition: none;
  cursor: grabbing;
}

.image-inner.is-dragging {
  cursor: grabbing;
}

.image-inner :deep(img) {
  cursor: inherit;
  user-select: none;
  -webkit-user-drag: none;
}
</style>
